import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useNavigate, useParams } from "react-router-dom";
import TaskQuest from "./TaskQuest";
import images from "../../../images";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import Button from "../../../components/common/Button/Button";
import {
  useGetQuestDetailsQuery,
  useLazyGetTakeQuestPracticeQuery,
  useGetQuestDetailsPublicQuery,
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
  usePostStartQuestMutation,
} from "../../../services/apiSlice";
import { toast } from "react-toastify";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../../components/common/Loading/LoadingSpinner";
import { CustomSelect } from "../../../components/common/Select/CustomSelect";
import { formatDateToFakeMicroUTC } from "../../../Utils/helper";
import { usePostHog } from "../../../hooks/usePostHog";

const QuestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const modalRef = useRef();
  const validateInfomationModelRef = useRef();
  const { t } = useTranslation();
  const { trackEvent, trackTimer } = usePostHog();
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const hasAccessToken = !!accessToken;
  const [isLoading, setIsLoading] = useState(false);
  const [showStartButton, setShowStartButton] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { data: questDetailsData, isFetching: isFetchingAuth } =
    useGetQuestDetailsQuery(
      { quest_id: id },
      {
        skip: !hasAccessToken,
        refetchOnMountOrArgChange: true,
      }
    );
  const { data: questDetailsDataPublic, isFetching: isFetchingPublic } =
    useGetQuestDetailsPublicQuery(
      { quest_id: id },
      {
        skip: hasAccessToken,
        refetchOnMountOrArgChange: true,
      }
    );

  const { data: userInfo, refetch } = useGetUserInfoQuery(
    {},
    {
      skip: !hasAccessToken,
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateUserInfo] = useUpdateUserInfoMutation();
  const [postStartQuest] = usePostStartQuestMutation();

  const questData = hasAccessToken ? questDetailsData : questDetailsDataPublic;
  const isFetching = hasAccessToken ? isFetchingAuth : isFetchingPublic;

  const thumbnailPaths =
    questData?.data.thumbnails?.map((thumbnail) => thumbnail.file_path) || [];
  const [onGetTakeQuestPractice, { isLoading: isLoadingPractice }] =
    useLazyGetTakeQuestPracticeQuery();

  const handleToggleTooltip = () => {
    setShowTooltip(!showTooltip);
  };
  const handleClickOutside = useCallback(
    (event) => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    },
    [showTooltip]
  );
  useEffect(() => {
    if (id) {
      // Track quest details viewed
      trackEvent('quest_viewed', {
        quest_id: id,
        has_access_token: hasAccessToken,
        viewing_type: hasAccessToken ? 'authenticated' : 'public'
      });

      onGetTakeQuestPractice({ quest_id: id })
        .unwrap()
        .catch((err) => {
          toast.error(
            err?.data?.detail?.[0]?.msg || "Failed to fetch quest practice"
          );
        });
    } else {
      toast.error("Quest ID is missing");
    }
  }, [id, onGetTakeQuestPractice, hasAccessToken, trackEvent]);

  // Track detailed quest information when data loads
  useEffect(() => {
    if (questData?.data) {
      trackEvent('quest_details_loaded', {
        quest_id: id,
        quest_type: questData.data.quest_type?.name,
        business_name: questData.data.quest_business?.business?.name,
        estimated_duration: questData.data.estimated_time,
        reward_coins: questData.data.token_reward,
        reward_xp: questData.data.xp_reward,
        requires_location: !!questData.data.location_required,
        difficulty_level: questData.data.difficulty,
        participant_count: questData.data.participant_count,
        thumbnail_count: thumbnailPaths.length,
        has_access_token: hasAccessToken
      });
    }
  }, [questData, id, thumbnailPaths.length, hasAccessToken, trackEvent]);

  useEffect(() => {
    if (showTooltip) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showTooltip, handleClickOutside]);
  const handleOpenModal = () => {
    modalRef?.current?.openModal();
    if (hasAccessToken && parseInt(userInfo?.data.xp) >= 10) {
      setShowStartButton(true);
    } else {
      setIsLoading(true);
      setShowStartButton(false);

      setTimeout(() => {
        setIsLoading(false);
        setShowStartButton(true);
      }, 3000);
    }
  };

  useEffect(() => {
    const handlePop = (event) => {
      navigate("/");
    };

    window.onpopstate = handlePop;

    return () => {
      window.onpopstate = null;
    };
  }, [navigate]);
  const handleTakeQuest = () => {
    const expireTime = questData?.data?.quest_business?.expire_time;

    if (expireTime) {
      const currentTime = new Date();
      const nowUTCISOString = formatDateToFakeMicroUTC(currentTime);
      const nowDate = new Date(nowUTCISOString.slice(0, 23));
      const questExpireTime = new Date(expireTime.slice(0, 23));

      if (nowDate > questExpireTime) {
        toast.error(
          t("quest.questDetails.questExpired") ||
            "This quest has expired and is no longer available."
        );
        return;
      }
    }
    if (userInfo?.data?.status !== "active" && hasAccessToken) {
      validateInfomationModelRef?.current?.openModal();
      return;
    }
    if (!hasAccessToken) {
      navigate("/register?from=qrcode&quest_id=" + id);
      return;
    }
    if (
      questData?.data?.consumer_status === "COMPLETED" &&
      questData?.data?.quest_business?.quest_type !== "SNAP" &&
      questData?.data?.number_handle_by_quota ===
        questData?.data?.quest_business?.quota_count
    ) {
      toast.info(t("quest.questDetails.questCompleted"));
      return;
    } else if (
      questData?.data?.consumer_status === "REVIEWING" &&
      questData?.data?.quest_business?.quest_type !== "SNAP"
    ) {
      toast.info(t("quest.questDetails.questUnderReviewing"));
    } else if (
      questData?.data?.slot_available === 0 &&
      questData?.data?.consumer_status === "NOT_STARTED"
    ) {
      toast.info(t("quest.questDetails.noSlotAvailable"));
      return;
    } else {
      handleOpenModal();
    }
  };

  const handleChoice = useCallback(async () => {
    if (!hasAccessToken) {
      toast.info(t("quest.questDetails.accessTokenRequired"));
      trackEvent('quest_start_failed', {
        quest_id: id,
        error_reason: 'no_access_token',
        quest_type: questData?.data?.quest_type?.name
      });
      return;
    }

    if (isLoadingPractice) {
      toast.info(
        t("quest.questDetails.loadingPracticeData") ||
          "Loading practice data..."
      );
      trackEvent('quest_start_failed', {
        quest_id: id,
        error_reason: 'loading_practice_data',
        quest_type: questData?.data?.quest_type?.name
      });
      return;
    }

    // Track quest start attempt
    const questStartTimer = trackTimer('quest_start_duration');
    trackEvent('quest_start_attempted', {
      quest_id: id,
      quest_type: questData?.data?.quest_type?.name,
      business_name: questData?.data?.quest_business?.business?.name,
      reward_coins: questData?.data?.token_reward,
      reward_xp: questData?.data?.xp_reward,
      consumer_status: questData?.data?.consumer_status,
      estimated_duration: questData?.data?.estimated_time,
      start_method: questData?.data?.consumer_status === "DOING" || questData?.data?.consumer_status === "PAUSED_QUEST" ? 'resume' : 'new'
    });

    // if (
    //   !dataPractice?.data ||
    //   !Array.isArray(dataPractice.data) ||
    //   dataPractice.data.length === 0
    // ) {
    //   toast.error(
    //     t("quest.questDetails.noPracticeData") || "No practice data available"
    //   );
    //   return;
    // }

    try {
      // Find the first non-COMPLETED item
      // const activeItem = dataPractice.data.find(
      //   (item) => item.status !== "COMPLETED"
      // );

      // if (!activeItem) {
      //   toast.error(
      //     t("quest.questDetails.allQuestsCompleted") ||
      //       "All quests are completed"
      //   );
      //   return;
      // }

      let response;
      if (
        questData?.data?.consumer_status === "DOING" ||
        questData?.data?.consumer_status === "PAUSED_QUEST"
      ) {
        const { data: practiceData } = await postStartQuest({
          quest_id: id,
        });
        response = practiceData;
      } else {
        response = await postStartQuest({
          quest_id: id,
        }).unwrap();
      }

      if (response?.status === 200) {
        questStartTimer.end({
          success: true,
          participant_id: response?.data?.id,
          quest_id: response?.data?.quest_id
        });

        trackEvent('quest_started', {
          quest_id: id,
          participant_id: response?.data?.id,
          quest_type: questData?.data?.quest_type?.name,
          business_name: questData?.data?.quest_business?.business?.name,
          reward_coins: questData?.data?.token_reward,
          reward_xp: questData?.data?.xp_reward,
          estimated_duration: questData?.data?.estimated_time,
          start_method: questData?.data?.consumer_status === "DOING" || questData?.data?.consumer_status === "PAUSED_QUEST" ? 'resume' : 'new',
          expired_at: response?.data?.expired_at,
          steps_done: response?.data?.stt_done
        });

        navigate(
          `/quest/details/multiple/choice/${response?.data?.id}?quest_id=${response?.data?.quest_id}&stt_done=${response?.data?.stt_done}&expired_at=${response?.data?.expired_at}&quotaType=${questData?.data?.quest_business?.quest_type}`
        );
      } else {
        throw new Error("Invalid response status");
      }
    } catch (err) {
      questStartTimer.end({
        success: false,
        error_code: err.status,
        error_message: err?.data?.detail?.[0]?.msg || err.message
      });

      trackEvent('quest_start_failed', {
        quest_id: id,
        error_reason: 'api_error',
        error_code: err.status,
        error_message: err?.data?.detail?.[0]?.msg || err.message,
        quest_type: questData?.data?.quest_type?.name
      });

      console.error("Error:", err);
      toast.error(
        err?.data?.detail?.[0]?.msg || t("toast.error.failedToTakeQuest")
      );
    }
  }, [
    hasAccessToken,
    isLoadingPractice,
    navigate,
    t,
    questData,
    postStartQuest,
    id,
    trackEvent,
    trackTimer,
  ]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const optionsAge = [
    { value: "", label: t("signUp.gender.gender") },
    { value: "male", label: t("signUp.gender.male") },
    { value: "female", label: t("signUp.gender.female") },
    { value: "maleIdentifying", label: t("signUp.gender.maleIdentifying") },
    { value: "femaleIdentifying", label: t("signUp.gender.femaleIdentifying") },
    { value: "nonBinary", label: t("signUp.gender.nonBinary") },
    { value: "agender", label: t("signUp.gender.agender") },
    { value: "preferNotToSay", label: t("signUp.gender.preferNotToSay") },
  ];

  const [formData, setFormData] = useState({
    age: "",
    gender: "",
  });
  const [errors, setErrors] = useState({
    age: "",
    gender: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, gender: selectedOption.value }));
    setErrors((prev) => ({ ...prev, gender: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.age || formData.age.trim() === "") {
      newErrors.age = t("onBoard.ageRequired") || "Age is required";
    } else if (parseInt(formData.age) < 13 || parseInt(formData.age) > 199) {
      newErrors.age =
        t("onBoard.ageInvalid") || "Age must be between 13 and 120";
    }

    if (!formData.gender || formData.gender === "") {
      newErrors.gender = t("onBoard.genderRequired") || "Gender is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValidateInformation = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userData = {
        age: parseInt(formData.age),
        gender: formData.gender,
      };

      const response = await updateUserInfo(userData).unwrap();
      if (response.status === 200) {
        validateInfomationModelRef?.current?.closeModal();
        await refetch();
        handleOpenModal();
      }
    } catch (error) {
      console.error("Error updating user info:", error);
      validateInfomationModelRef?.current?.closeModal();
      isFetching();
      toast.error(
        error?.data?.detail?.[0]?.msg || "Failed to update information"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return <div>{t("quest.questDetails.errorLoadingMap")}</div>;
  }

  if (!isLoaded) {
    return <LoadingCommon />;
  }

  return (
    <>
      <LoadingCommon statusLoading={isFetching || isLoadingPractice} />
      <div className="flex flex-col mx-auto w-full max-w-[428px]">
        <div className="flex flex-col w-full ">
          <TaskQuest
            id={questData?.data?.id}
            imagesNameShop={images.ImageNameShop}
            questName={questData?.data?.quest_business?.name || ""}
            shopName={questData?.data?.quest_business?.name || ""}
            branchName={""}
            description={questData?.data?.quest_business?.description || ""}
            questType={
              questData?.data?.quest_business?.quest_type || "INTRODUCE"
            }
            requirement={questData?.data.quest_business?.requirement || ""}
            estTime={questData?.data?.quest_business?.est_time}
            token={questData?.data?.quest_business?.token}
            expireIn={
              questData?.data?.participants &&
              Array.isArray(questData?.data?.participants) &&
              questData?.data?.participants.length > 0
                ? questData?.data?.participants[0].expired_at
                : null
            }
            quotaType={questData?.data?.quest_business?.quota_type ?? ""}
            expireTime={questData?.data?.quest_business?.expire_time || null}
            slotAvailable={questData?.data?.slot_available}
            maxSlots={hasAccessToken ? questData?.data?.max_slot : null}
            numberHandleByQuota={questData?.data?.number_handle_by_quota ?? 0}
            quotaCount={questData?.data?.quest_business?.quota_count ?? 0}
            isFavorited={questData?.data?.is_favorited || false}
            thumbnails={thumbnailPaths}
            businessId={questData?.data?.quest_business?.business_id}
            isEnable={questData?.data?.is_enable}
            consumerStatus={
              hasAccessToken ? questData?.data?.consumer_status : "NOT_STARTED"
            }
            handleTaskQuest={handleTakeQuest}
            businessAvatar={
              questData?.data?.quest_business?.business?.avatar_data?.file_path
            }
            businessName={
              questData?.data?.quest_business.business.company_name || ""
            }
            address={questData?.data?.location.address || ""}
            name={questData?.data?.quest_business.name || ""}
            xp={questData?.data?.quest_business.xp || 0}
            locationId={questData?.data?.location.id || null}
          />
        </div>

        <CommonModal
          ref={modalRef}
          title={t("quest.questDetails.yourMission")}
          classNameText="text-[14px] font-medium text-[#253FF6]"
          showCloseIcon={true}
          closeIcon={images.CloseModalIconGray}
          actions={(closeModal) => (
            <div className="flex justify-center w-full">
              {isLoading ? (
                <div className="flex justify-center items-center bg-[#ECEEFF] px-[18px] py-3 rounded-md w-[308px] h-[50px]">
                  <LoadingSpinner size="xs" />
                </div>
              ) : showStartButton ? (
                <Button
                  text={t("quest.questDetails.gotItLestGo")}
                  className="w-full px-[18px] py-3 rounded-md min-h-[54px] text-white bg-[#253FF6] font-semibold"
                  onClick={handleChoice}
                />
              ) : (
                <div className="flex gap-3.5 justify-center mt-2 w-full text-sm tracking-tight">
                  <Button
                    text={t("base.cancel")}
                    className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-[#A0A0A0]"
                    onClick={() => modalRef.current?.closeModal()}
                  />
                  <Button
                    text={t("base.yes")}
                    className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#253FF6]"
                    variant="primary"
                    onClick={handleChoice}
                  />
                </div>
              )}
            </div>
          )}
        >
          <div className="flex flex-col justify-center gap-2 w-full">
            <div className="flex justify-center items-center">
              <img
                src={images.Illustrations}
                alt=""
                className="w-[170px] h-[224.031px]"
              />
            </div>
            <div className="flex flex-col justify-center font-medium text-[20px] w-full text-center text-[#253FF6]">
              <div className="flex justify-center items-center gap-1">
                <p>{t("quest.questDetails.provideQualityFeedback")}</p>
                <div className="relative inline-block">
                  <img
                    src={images.CircleHelpIconBlue}
                    alt=""
                    className="w-[21px] h-[21px] cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleTooltip();
                    }}
                  />
                  {showTooltip && (
                    <div className="absolute bottom-full right-[125px] transform translate-x-1/2 mb-2 z-[20]">
                      <div className="bg-[#ECEEFF] rounded-lg p-4 w-[308px]">
                        <div className="absolute top-full right-8 transform">
                          <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#ECEEFF]"></div>
                        </div>
                        <div className="text-left ml-[20px]">
                          <p className="text-[#253FF6] text-xs font-medium leading-relaxed">
                            {t("quest.questDetails.tooltipContent")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <p>{t("quest.questDetails.toEarnRewards")}</p>
              </div>
            </div>
            <div className="text-center text-[#333] text-lg font-medium">
              <p>
                {t("quest.questDetails.qualityFeedback")} = 💰
                <br />
                <span>{t("quest.questDetails.vagueFeedback")} = ❌</span>
              </p>
            </div>
            <div className="text-center text-sm text-[#A0A0A0] font-medium">
              <p>{t("quest.questDetails.yourSubmissionWillReviewed")}</p>
            </div>
          </div>
        </CommonModal>

        <CommonModal
          ref={validateInfomationModelRef}
          classNameText="text-[#201D1B] font-medium text-lg"
          canClickOverLay={false}
          showCloseIcon={true}
          closeIcon={images.CloseModalIconGray}
        >
          <div className="flex flex-col justify-start items-start gap-[18px]">
            <h2 className="text-[#253FF6] text-4xl leading-[2.5rem] font-bold whitespace-pre-line">
              {t("onBoard.questMadeForYou")}
            </h2>
            <p className="text-xl font-medium text-[#253FF6] whitespace-pre-line">
              {t("onBoard.shareYourInfo")}
            </p>
            <div className="flex flex-col items-start w-full">
              <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 w-full">
                <img
                  src={images.CalendarIcon2}
                  alt="Icon"
                  className="w-5 h-5 mr-3"
                />
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder={t("onBoard.age")}
                  className="w-full px-4 py-1 text-base focus:outline-none focus:border-transparent"
                />
              </div>
              {errors.age && (
                <p className="text-red-500 text-sm mt-1">{errors.age}</p>
              )}
            </div>
            <div className="flex flex-col items-start w-full">
              <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 w-full">
                <img
                  src={images.User3Icon}
                  alt="Icon"
                  className="w-5 h-5 mr-3"
                />
                <CustomSelect
                  options={optionsAge}
                  value={formData.gender}
                  onChange={handleSelectChange}
                  placeholder={t("onBoard.gender")}
                  icon={""}
                />
              </div>
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
              )}
            </div>
            <button
              className={`w-full px-4 py-3 text-white rounded-lg text-lg font-medium transition-all ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#253FF6] hover:bg-[#1e32cc]"
              }`}
              onClick={handleValidateInformation}
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner size="xs" /> : t("base.next")}
            </button>
          </div>
        </CommonModal>
      </div>
    </>
  );
};

export default QuestDetails;
